import { useEffect, useMemo, useState } from "react";
import {
  App,
  Button,
  Card,
  Input,
  List,
  Popconfirm,
  Space,
  Typography,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuth } from "../../lib/auth";
import type { Stage } from "../../lib/types";

interface StagesCardProps {
  projectId: string;
}

export default function StagesCard({ projectId }: StagesCardProps) {
  const { message } = App.useApp();
  const { isAdmin, canWrite } = useAuth();
  const [stages, setStages] = useState<Stage[]>([]);
  const [newStageName, setNewStageName] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyStageId, setBusyStageId] = useState<string | null>(null);

  useEffect(() => {
    const stagesQuery = query(
      collection(db, "projectStages"),
      where("projectId", "==", projectId),
      orderBy("order", "asc"),
    );
    const unsubscribe = onSnapshot(stagesQuery, (snapshot) => {
      const data: Stage[] = [];
      snapshot.forEach((stageDoc) => {
        const raw = stageDoc.data();
        data.push({
          id: stageDoc.id,
          projectId: raw.projectId,
          name: raw.name ?? "",
          order: typeof raw.order === "number" ? raw.order : 0,
          createdAt: raw.createdAt ?? "",
        });
      });
      setStages(data);
    });
    return () => unsubscribe();
  }, [projectId]);

  const maxOrder = useMemo(
    () => stages.reduce((max, stage) => Math.max(max, stage.order), -1),
    [stages],
  );

  const handleAddStage = async () => {
    const name = newStageName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await addDoc(collection(db, "projectStages"), {
        projectId,
        name,
        order: maxOrder + 1,
        createdAt: new Date().toISOString(),
      });
      setNewStageName("");
    } catch (error) {
      console.error("Failed to add stage:", error);
      message.error("Failed to add stage. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  const handleRenameStage = async (stage: Stage, nextName: string) => {
    const trimmed = nextName.trim();
    if (!trimmed || trimmed === stage.name) return;
    try {
      await updateDoc(doc(db, "projectStages", stage.id), { name: trimmed });
    } catch (error) {
      console.error("Failed to rename stage:", error);
      message.error("Failed to rename stage. Please try again.");
    }
  };

  const swapStages = async (a: Stage, b: Stage) => {
    setBusyStageId(a.id);
    try {
      await Promise.all([
        updateDoc(doc(db, "projectStages", a.id), { order: b.order }),
        updateDoc(doc(db, "projectStages", b.id), { order: a.order }),
      ]);
    } catch (error) {
      console.error("Failed to reorder stages:", error);
      message.error("Failed to reorder stages. Please try again.");
    } finally {
      setBusyStageId(null);
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index <= 0) return;
    await swapStages(stages[index], stages[index - 1]);
  };

  const handleMoveDown = async (index: number) => {
    if (index >= stages.length - 1) return;
    await swapStages(stages[index], stages[index + 1]);
  };

  const countTasksUsingStage = async (stageId: string) => {
    const tasksQuery = query(
      collection(db, "projectTasks"),
      where("projectId", "==", projectId),
      where("stageId", "==", stageId),
    );
    try {
      const countSnap = await getCountFromServer(tasksQuery);
      return countSnap.data().count;
    } catch (error) {
      // Aggregation queries are not supported in the Firestore emulator
      // for some configurations. Fall back to a full read.
      console.warn(
        "getCountFromServer failed; falling back to getDocs():",
        error,
      );
      const docsSnap = await getDocs(tasksQuery);
      return docsSnap.size;
    }
  };

  const handleDeleteStage = async (stage: Stage) => {
    setBusyStageId(stage.id);
    try {
      const usageCount = await countTasksUsingStage(stage.id);
      if (usageCount > 0) {
        message.warning(
          `Cannot delete — ${usageCount} task${
            usageCount === 1 ? "" : "s"
          } use this stage. Reassign or delete those tasks first.`,
        );
        return;
      }
      await deleteDoc(doc(db, "projectStages", stage.id));
      message.success("Stage deleted.");
    } catch (error) {
      console.error("Failed to delete stage:", error);
      message.error("Failed to delete stage. Please try again.");
    } finally {
      setBusyStageId(null);
    }
  };

  return (
    <Card title="Stages">
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {stages.length === 0 ? (
          <Typography.Text type="secondary">
            No stages yet. Add at least one stage so Tasks can be created
            against it.
          </Typography.Text>
        ) : (
          <List
            aria-label="Project stages list"
            dataSource={stages}
            renderItem={(stage, index) => (
              <List.Item
                key={stage.id}
                actions={[
                  ...(canWrite
                    ? [
                        <Button
                          key="up"
                          type="text"
                          icon={<ArrowUpOutlined />}
                          aria-label={`Move stage ${stage.name} up`}
                          disabled={index === 0 || busyStageId === stage.id}
                          onClick={() => handleMoveUp(index)}
                        />,
                        <Button
                          key="down"
                          type="text"
                          icon={<ArrowDownOutlined />}
                          aria-label={`Move stage ${stage.name} down`}
                          disabled={
                            index === stages.length - 1 || busyStageId === stage.id
                          }
                          onClick={() => handleMoveDown(index)}
                        />,
                      ]
                    : []),
                  ...(isAdmin
                    ? [
                        <Popconfirm
                          key="delete"
                          title="Delete stage"
                          description={`Delete stage "${stage.name}"?`}
                          okText="Delete"
                          okButtonProps={{ danger: true }}
                          cancelText="Cancel"
                          onConfirm={() => handleDeleteStage(stage)}
                        >
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            aria-label={`Delete stage ${stage.name}`}
                            disabled={busyStageId === stage.id}
                          />
                        </Popconfirm>,
                      ]
                    : []),
                ]}
              >
                <Typography.Text
                  editable={
                    canWrite
                      ? {
                          onChange: (next) => handleRenameStage(stage, next),
                          maxLength: 60,
                          tooltip: "Click to rename stage",
                        }
                      : false
                  }
                >
                  {stage.name}
                </Typography.Text>
              </List.Item>
            )}
          />
        )}

        {canWrite && (
          <Space.Compact style={{ width: "100%" }}>
            <Input
              value={newStageName}
              onChange={(event) => setNewStageName(event.target.value)}
              onPressEnter={handleAddStage}
              placeholder="New stage name"
              aria-label="New stage name"
              maxLength={60}
              disabled={adding}
            />
            <Button
              type="primary"
              onClick={handleAddStage}
              disabled={!newStageName.trim() || adding}
              loading={adding}
              aria-label="Add stage"
            >
              Add
            </Button>
          </Space.Compact>
        )}
      </Space>
    </Card>
  );
}
